import { useEffect, useState } from 'react';
import { MediaContext } from './Media';

export interface IMediaContainerProps {
  children: any;
}

export const MediaContainer = ({ children }: IMediaContainerProps) => {
  const [cameraList, setCameraList] = useState<MediaDeviceInfo[] | undefined>(undefined);
  const [canvas] = useState<HTMLCanvasElement>(document.createElement('canvas'));
  const [hasCameraStarted, setHasStartedCamera] = useState<boolean>(false);
  const [isCameraAllowed, setIsCameraAllowed] = useState<boolean | undefined>(undefined);
  // True only when the browser reports a standing block, meaning we can no
  // longer re-prompt programmatically and the user must unblock in site
  // settings. Kept separate from isCameraAllowed so the UI can offer a
  // one-click "Allow" button while access is still re-promptable.
  const [isCameraBlocked, setIsCameraBlocked] = useState<boolean>(false);
  // True once access is actually granted. Lets the UI show the explicit
  // "Allow camera access" button in the not-yet-granted states (prompt or a
  // still-re-promptable denial) and hide it once there's nothing left to ask.
  const [isCameraGranted, setIsCameraGranted] = useState<boolean>(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | undefined>(undefined);
  const [selectedCamera, setSelectedCamera] = useState<string | undefined>(undefined);
  const [video, setVideo] = useState<HTMLVideoElement | undefined>(undefined);

  useEffect(() => {
    allowCamera();
    listDevices();
    return () => closeStream();
  }, [])

  const allowCamera = async () => {
    // Only read the permission state on mount. We deliberately do NOT probe
    // with getUserMedia here: a transient device error (camera busy, held by
    // another tab/app) would otherwise flip isCameraAllowed to false and
    // permanently grey out the button even though permission is granted.
    try {
      const cameraPermissions = await window.navigator.permissions.query({ name: 'camera' as PermissionName });
      const sync = () => {
        setIsCameraAllowed(cameraPermissions.state !== 'denied');
        setIsCameraBlocked(cameraPermissions.state === 'denied');
        setIsCameraGranted(cameraPermissions.state === 'granted');
      };
      sync();
      // Reflect grant/revoke from the browser UI without a page reload.
      cameraPermissions.onchange = sync;
    } catch (err) {
      // Some browsers (e.g. Firefox/Safari) don't support the 'camera'
      // Permissions descriptor. Assume allowed; startCamera() will surface the
      // real prompt/denial on first use.
      setIsCameraAllowed(true);
    }
  }

  // Explicitly re-request camera access from a user gesture (button press).
  // In the "prompt" / "not yet asked" state this pops the native browser
  // permission dialog; if the user has already blocked the camera the call
  // rejects and we flag it as blocked so the UI can show unblock guidance.
  const requestCameraAccess = async (): Promise<boolean> => {
    try {
      const stream = await window.navigator.mediaDevices.getUserMedia({ video: true });
      // Release the probe stream immediately; startCamera() opens the real one.
      stream.getTracks().forEach((track) => track.stop());
      setIsCameraAllowed(true);
      setIsCameraBlocked(false);
      setIsCameraGranted(true);
      return true;
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        setIsCameraAllowed(false);
        // Confirm whether this is a standing block (no future prompt possible)
        // versus a one-off dismissal (still re-promptable).
        try {
          const p = await window.navigator.permissions.query({ name: 'camera' as PermissionName });
          setIsCameraBlocked(p.state === 'denied');
        } catch {
          setIsCameraBlocked(true);
        }
      }
      console.error('Camera permission request failed:', err);
      return false;
    }
  };

  const changeCamera = async (deviceId: string) => {
    setHasStartedCamera(true);
    const stream = await window.navigator.mediaDevices.getUserMedia({ video: { deviceId } });
    setMediaStream(stream);
    const videoElement = document.querySelector('video#video');
    if (!videoElement) {
      return;
    }
    (videoElement as any).srcObject = stream;
    setVideo(videoElement);
  }

  const closeStream = async () => {
    mediaStream?.getVideoTracks()?.forEach((track: MediaStreamTrack) => {
      track.stop();
      mediaStream?.removeTrack(track);
    });

    setHasStartedCamera(false);
    setMediaStream(undefined);
    setVideo(undefined);
  }

  const listDevices = async () => {
    const deviceList = await window.navigator.mediaDevices.enumerateDevices();
    setCameraList(deviceList.filter((device) => device.kind === 'videoinput'));
  }

  const retakePhoto = (setPhotoUriList: (photoUriList: string[]) => void) => {
    setPhotoUriList([]);
    setHasStartedCamera(false);
  }

  const startCamera = async () => {
    // Bail out early only on an explicit, standing denial.
    try {
      const cameraPermissions = await window.navigator.permissions.query({ name: 'camera' as PermissionName });
      if (cameraPermissions.state === 'denied') {
        setIsCameraAllowed(false);
        setHasStartedCamera(false);
        return;
      }
    } catch (err) {
      // Permissions API unavailable for 'camera'; fall through and let
      // getUserMedia trigger the browser prompt.
    }

    try {
      // getUserMedia itself shows the prompt when the state is 'prompt' and
      // resolves once the user grants access.
      const stream = await window.navigator.mediaDevices.getUserMedia({
        video: selectedCamera ? { deviceId: selectedCamera } : true,
      });
      setIsCameraAllowed(true);
      setIsCameraGranted(true);
      setHasStartedCamera(true);
      await listDevices();
      setMediaStream(stream);
      const videoElement = document.querySelector('video#video');
      if (!videoElement) {
        return;
      }
      (videoElement as any).srcObject = stream;
      setVideo(videoElement);
    } catch (err: any) {
      // Only a real permission denial disables the camera. Device errors
      // (busy hardware, no camera) leave the button usable for a retry.
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        setIsCameraAllowed(false);
        setIsCameraGranted(false);
      }
      setHasStartedCamera(false);
      console.error('Unable to start camera:', err);
    }
  };

  const takePhoto = (setPhotoUriList: (photoUriList?: string[]) => void) => {
    if (!video) {
      return;
    }

    let context;
    const width = video.offsetWidth
    const height = video.offsetHeight;

    canvas.width = width;
    canvas.height = height;

    context = canvas.getContext('2d')!;
    context.drawImage(video, 0, 0, width, height);

    closeStream();
    setPhotoUriList([canvas.toDataURL('image/png')]);
  }

  const mySetMediaStream = (stream: MediaStream | undefined) => {
    setMediaStream(stream);
  }

  const mySetVideo = (htmlVideo: HTMLVideoElement | undefined) => {
    setVideo(htmlVideo);
  }

  return (
    <MediaContext.Provider value={{
      cameraList,
      changeCamera,
      closeStream,
      hasCameraStarted,
      isCameraAllowed,
      isCameraBlocked,
      isCameraGranted,
      mediaStream,
      requestCameraAccess,
      selectedCamera,
      setMediaStream: mySetMediaStream,
      setSelectedCamera,
      setVideo: mySetVideo,
      retakePhoto,
      startCamera,
      takePhoto,
      video,
    }}>
      {
        children
      }
    </MediaContext.Provider>
  );
}
